import { useState } from 'react';
import { Quote, FileText, ChevronDown, ChevronUp, Copy, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { CitedPassage } from '@/features/evaluation/types';

// =========================================
// PassageCitation — single cited passage
// =========================================

interface PassageCitationProps {
  passage: CitedPassage;
  index?: number;
  criteriaName?: string;
  validation?: { status: 'verified' | 'unverified' | 'not_found'; similarity: number };
  className?: string;
}

export function PassageCitation({
  passage,
  index,
  criteriaName,
  validation,
  className,
}: PassageCitationProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(passage.text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      className={cn(
        'group relative border-l-3 border-indigo-400 bg-indigo-50/60 rounded-r-lg p-3 hover:bg-indigo-50 transition-colors',
        className
      )}
    >
      <div className="flex items-start gap-2">
        <Quote className="h-4 w-4 text-indigo-400 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0 space-y-1.5">
          <p className="text-sm text-slate-700 leading-relaxed italic">
            &ldquo;{passage.text}&rdquo;
          </p>
          <div className="flex items-center flex-wrap gap-2">
            {passage.page !== null && (
              <Badge variant="outline" className="text-[10px] gap-1">
                <FileText className="h-3 w-3" />
                Page {passage.page}
              </Badge>
            )}
            {criteriaName && (
              <Badge variant="secondary" className="text-[10px]">
                {criteriaName}
              </Badge>
            )}
            {index !== undefined && (
              <span className="text-[10px] text-slate-400">
                #{index + 1}
              </span>
            )}
            {validation && (
              <Badge
                variant="outline"
                className={cn(
                  'text-[10px]',
                  validation.status === 'verified'
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : validation.status === 'unverified'
                      ? 'bg-amber-50 text-amber-700 border-amber-200'
                      : 'bg-rose-50 text-rose-700 border-rose-200'
                )}
              >
                {validation.status === 'verified'
                  ? 'Verified'
                  : validation.status === 'unverified'
                    ? `Low match ${Math.round(validation.similarity * 100)}%`
                    : 'Not found'}
              </Badge>
            )}
          </div>
          {passage.relevance && (
            <p className="text-xs text-slate-500">{passage.relevance}</p>
          )}
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
              onClick={handleCopy}
            >
              {copied ? (
                <Check className="h-3.5 w-3.5 text-emerald-500" />
              ) : (
                <Copy className="h-3.5 w-3.5 text-slate-400" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-xs">{copied ? 'Copied!' : 'Copy passage'}</p>
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
}

// =========================================
// PassageCitationList — collapsible list
// =========================================

interface PassageCitationListProps {
  passages: CitedPassage[];
  criteriaNames?: Record<string, string>;
  maxVisible?: number;
  className?: string;
}

export function PassageCitationList({
  passages,
  criteriaNames,
  maxVisible = 3,
  className,
}: PassageCitationListProps) {
  const [showAll, setShowAll] = useState(false);

  if (passages.length === 0) {
    return (
      <div className={cn('text-sm text-slate-400 italic py-4 text-center', className)}>
        No cited passages available
      </div>
    );
  }

  const visible = showAll ? passages : passages.slice(0, maxVisible);
  const hiddenCount = passages.length - maxVisible;

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Quote className="h-4 w-4 text-indigo-500" />
            Cited Passages
          </CardTitle>
          <Badge variant="outline" className="text-xs">
            {passages.length} passage{passages.length !== 1 ? 's' : ''}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {visible.map((passage, i) => (
          <PassageCitation
            key={i}
            passage={passage}
            index={i}
            criteriaName={
              passage.criteria_id && criteriaNames
                ? criteriaNames[passage.criteria_id]
                : undefined
            }
          />
        ))}
        {hiddenCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-xs text-slate-500"
            onClick={() => setShowAll((prev) => !prev)}
          >
            {showAll ? (
              <>
                <ChevronUp className="h-3.5 w-3.5 mr-1" />
                Show less
              </>
            ) : (
              <>
                <ChevronDown className="h-3.5 w-3.5 mr-1" />
                Show {hiddenCount} more passage{hiddenCount !== 1 ? 's' : ''}
              </>
            )}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
